import React, { memo, useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

interface ISeriesProps {
  labels: string[];
  values: number[];
}

interface ITwoSeriesProps {
  labels: string[];
  aValues: number[];
  bValues: number[];
  aLabel: string;
  bLabel: string;
  aColor?: string;
  bColor?: string;
}

const PALETTE = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#9333ea',
  '#ef4444',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#7c3aed',
  '#dc2626',
];

const COMPACT_LEGEND_PADDING = 8;

/** Single-series month bar chart used by «Expenses by month» on the dashboard. */
export const MonthBarChart = memo(({ labels, values }: ISeriesProps) => {
  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    [],
  );

  return (
    <Bar
      options={options}
      data={{
        labels,
        datasets: [
          {
            label: 'PLN',
            data: values,
            backgroundColor: '#2563eb',
            borderRadius: 4,
          },
        ],
      }}
    />
  );
});

/** Category / legal-entity breakdown — donut with auto-cycled palette. */
export const CategoryDonut = memo(({ labels, values }: ISeriesProps) => {
  const options: ChartOptions<'doughnut'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } },
    }),
    [],
  );
  return (
    <Doughnut
      options={options}
      data={{
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]),
            borderWidth: 1,
          },
        ],
      }}
    />
  );
});

/**
 * Grouped two-series bar chart — drives «Income vs Expenses» and «Issued vs
 * Received» on the dashboard. Two parallel bars per x-tick, colours fixed
 * per call so the legend stays readable across re-renders.
 */
export const GroupedBarChart = memo(
  ({
    labels,
    aValues,
    bValues,
    aLabel,
    bLabel,
    aColor = '#16a34a',
    bColor = '#ef4444',
  }: ITwoSeriesProps) => {
    const options: ChartOptions<'bar'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { padding: COMPACT_LEGEND_PADDING },
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      }),
      [],
    );

    return (
      <Bar
        options={options}
        data={{
          labels,
          datasets: [
            {
              label: aLabel,
              data: aValues,
              backgroundColor: aColor,
              borderRadius: 4,
            },
            {
              label: bLabel,
              data: bValues,
              backgroundColor: bColor,
              borderRadius: 4,
            },
          ],
        }}
      />
    );
  },
);

/**
 * Line chart with negative-value awareness — used for the «Profit» card.
 * Points where the value is below zero are filled red to draw the eye to
 * months where expenses outran income.
 */
export const LineWithZeroChart = memo(({ labels, values }: ISeriesProps) => {
  const pointColors = useMemo(
    () => values.map((v) => (v < 0 ? '#dc2626' : '#16a34a')),
    [values],
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false } },
        y: {
          ticks: { precision: 0 },
          grid: { color: '#f1f5f9' },
        },
      },
    }),
    [],
  );

  return (
    <Line
      options={options}
      data={{
        labels,
        datasets: [
          {
            label: 'PLN',
            data: values,
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37,99,235,0.12)',
            pointBackgroundColor: pointColors,
            pointBorderColor: pointColors,
            pointRadius: 4,
            tension: 0.25,
            fill: true,
          },
        ],
      }}
    />
  );
});
